// src/components/AvatarUploader.tsx
// Drag & drop avatar upload with preview.

import { useState, useCallback } from 'react';
import { cn } from '@core/ui';

interface AvatarUploaderProps {
  currentUrl?: string;
  onFileSelect: (file: File) => void;
  accept?: string;
  maxSizeMb?: number;
  className?: string;
  hideHint?: boolean;
  initials?: string;
}

export function AvatarUploader({
  currentUrl,
  onFileSelect,
  accept = 'image/jpeg,image/png,image/webp',
  maxSizeMb = 5,
  className,
  hideHint = false,
  initials,
}: AvatarUploaderProps) {
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState('');

  const handleFile = useCallback(
    (file: File) => {
      setError('');
      if (file.size > maxSizeMb * 1024 * 1024) {
        setError(`File must be under ${maxSizeMb}MB`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(file);
      onFileSelect(file);
    },
    [maxSizeMb, onFileSelect],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const displayUrl = previewUrl || currentUrl;

  return (
    <div className={cn('flex flex-col items-center gap-4', className)}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={cn(
          'relative w-32 h-32 rounded-full overflow-hidden cursor-pointer',
          'border-2 transition-colors',
          dragOver ? 'border-primary bg-primary/5' : 'border-transparent',
          'hover:border-primary/50',
        )}
      >
        {displayUrl ? (
          <img src={displayUrl} alt="Avatar" className="w-full h-full object-cover" />
        ) : (
          <div className="flex items-center justify-center w-full h-full bg-muted">
            {initials ? (
              <span className="text-2xl font-bold text-muted-foreground select-none">{initials}</span>
            ) : (
              <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            )}
          </div>
        )}
        <div className="absolute inset-0 bg-black/40 opacity-60 hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        {/* Transparent file input covering entire area — click triggers native file dialog */}
        <input
          type="file"
          accept={accept}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
        />
      </div>
      {error && <span className="text-xs text-destructive">{error}</span>}
      {!hideHint && <span className="text-xs text-muted-foreground">JPG, PNG or WebP. Max {maxSizeMb}MB.</span>}
    </div>
  );
}
